import { IController } from 'angular';
import * as _ from 'lodash';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { ImStudent } from '../../../../../ng2/shared/services/im-models/im-student.service';
import { IStudent } from '../../../../../ng2/shared/typings/interfaces/student.interface';
import { ImStudentCreditGaps } from './../../../../shared/static-models/im-models/im-student-credit-gaps';
import { ImStudentCurrentProgram } from './../../../../shared/static-models/im-models/im-student-current-program';
import { ImStudentRegents } from './../../../../shared/static-models/im-models/im-student-regents';

class StudentSnapshotPanelCtrl implements IController {
  static $inject = [
    '$scope',
    'SorterColumn',
    'ImStudentCreditGaps',
    'ImStudent',
    'ImStudentRegents',
    'ImStudentCurrentProgram',
    'UtilitiesService',
  ];

  // bindings
  public student: IStudent;

  // local
  public cards;

  constructor(
    private $scope,
    private SorterColumn,
    private imStudentCreditGaps: ImStudentCreditGaps,
    private imStudent: ImStudent,
    private imStudentRegents: ImStudentRegents,
    private imStudentCurrentProgram: ImStudentCurrentProgram,
    private utilitiesService: UtilitiesService,
  ) {}

  $onInit() {
    // REGENTS CARD DATA
    const regentsItemsToDisplay = {
      passedOf5Local: {
        label: 'Passed of 5 Local',
        value: this.SorterColumn.getByColumnKey('countOfRegentsPassedAt55Of5SpedOnly').calculation(this.student),
      },
      passedOf5Regents: {
        label: 'Passed of 5 Regents',
        value: this.utilitiesService.getFieldByPath(
          this.student,
          this.SorterColumn.getByColumnKey('countOfRegentsPassedAt65Of5').path,
        ),
      },
      passedOf9Advanced: {
        label: 'Passed of 9 Advanced',
        value: this.utilitiesService.getFieldByPath(
          this.student,
          this.SorterColumn.getByColumnKey('countOfRegentsPassedAt65Of9').path,
        ),
      },
      examsPlanningToSitFor: {
        label: 'Exams Planned for Next Admin',
        value: this.SorterColumn.getByColumnKey('countOfRegentsSchedForNextAdmin').calculation(this.student),
      },
    };

    // CREDITS CARD DATA
    const creditsItemsToDisplay = {
      creditsEarned: {
        label: 'Credits Earned',
        value: this.student.creditDetails.byArea.total.earned,
      },
      creditsScheduled: {
        label: 'Credits Scheduled',
        value: this.student.creditDetails.byArea.total.scheduled,
      },
      coursesScheduled: {
        label: 'Credit Gaps',
        value: this.student.creditDetails.creditGaps.maxGaps || 0,
      },
      coursesFailing: {
        label: 'Courses Failing',
        value:
          (this.student.creditDetails.currSy.mostRecentMps.totalFailing || 0) +
          ' (' +
          (this.student.creditDetails.currSy.mostRecentMps.totalFailingHighPriority || 0) +
          ' High Priority)',
      },
    };

    // ATTENDANCE CARD DATA
    const attendanceItemsToDisplay = {
      yearToDate: {
        label: 'Year To Date',
        value: this._formatPercentage(this.student.attendanceDetails.currSy.ytd.percentPresent),
      },
      percentLate: {
        label: 'Percent Late',
        value: this._formatPercentage(this.student.attendanceDetails.currSy.ytd.percentLate),
      },
      lastYear: {
        label: 'Last Year',
        value: this._formatPercentage(this.student.attendanceDetails.priorSy.finalPercentPresent),
      },
      absLate10Days: {
        label: 'Absences Last 10 Days',
        value: this.student.attendanceDetails.currSy.last10Days.abs,
      },
    };

    // COLLEGE READINESS CARD DATA
    const collegeReadinessItemsToDisplay = {
      ela: {
        label: 'CUNY Ready ELA',
        value: this.utilitiesService.getFieldByPath(
          this.student,
          this.SorterColumn.getByColumnKey('collegeReadyOnElaRegentsOrSatCr').path,
        ),
      },
      math: {
        label: 'CUNY Ready Math',
        value: this.utilitiesService.getFieldByPath(
          this.student,
          this.SorterColumn.getByColumnKey('collegeReadyOnMathRegentsOrSatMath').path,
        ),
      },
      maxSat: {
        label: 'Max SAT',
        value:
          this.utilitiesService.getFieldByPath(
            this.student,
            this.SorterColumn.getByColumnKey('satMaxScoreTotalReadingMath').path,
          ) || 'na',
      },
      maxActEnglish: {
        label: 'Max ACT',
        // tslint:disable-next-line:max-line-length
        value:
          'English: ' +
          (this.student.charterDetails.maxScoreActMath || 'na') +
          ' -- Math: ' +
          (this.student.charterDetails.maxScoreActMath || 'na'),
      },
    };

    const diplomaType = this.student.gradPlanningDetails.plannedDiplomaType;

    this.cards = [
      new _Card(
        'Regents',
        this.imStudentRegents.getRegentsOnTrackStatus(this.student, diplomaType),
        regentsItemsToDisplay,
      ),
      new _Card('Credits', this.imStudentCreditGaps.isCreditsOnTrack(this.student), creditsItemsToDisplay),
      new _Card('Attendance', this.imStudent.isAttendanceOnTrack(this.student), attendanceItemsToDisplay),
      new _Card(
        'College Readiness',
        this.imStudent.isCollegeReadinessOnTrack(this.student),
        collegeReadinessItemsToDisplay,
      ),
    ];

    // Update Regents Card when "plannedDiplomaType" is changed
    this.$scope.$watch(
      () => {
        return this.student.gradPlanningDetails.plannedDiplomaType;
      },
      () => {
        this.cards[0].setOnTrackStatus(this.imStudentRegents.getRegentsOnTrackStatus(this.student, diplomaType));
      },
      true,
    );

    // Update Regents Card when "nextScheduledRegents" is changed
    this.$scope.$watch(
      () => {
        return this.student.nextScheduledRegents;
      },
      () => {
        this.cards[0].items[3].value = this.SorterColumn.getByColumnKey('countOfRegentsSchedForNextAdmin').calculation(
          this.student,
        );
      },
      true,
    );

    // Update Regents Card when "schoolVerifiedSafetyNetEligibility" is changed
    this.$scope.$watch(
      () => {
        return this.student.gradPlanningDetails.schoolVerifiedSafetyNetEligibility;
      },
      () => {
        this.cards[0].items[0].value = this.SorterColumn.getByColumnKey(
          'countOfRegentsPassedAt55Of5SpedOnly',
        ).calculation(this.student);
      },
      true,
    );
  }

  // methods
  _formatPercentage(number) {
    return (number * 100).toFixed(2) + '%';
  }
}

function _Card(title, onTrackStatus, items) {
  this.setOnTrackStatus(onTrackStatus); // sets this.onTrackStatus

  this.title = title;
  this.items = _.map(items, function(item: any) {
    return {
      label: item.label,
      value: item.value,
    };
  });
}

_Card.prototype.setOnTrackStatus = function(onTrackStatus) {
  // Temporarily set all but Regents as `Coming-Soon!` remove if block after functionality is added:
  if (this.title !== 'Regents') {
    this.onTrackStatus = 'Coming Soon!';
  } else {
    this.onTrackStatus = onTrackStatus.status.toUpperCase();
  }
};

angular.module('nv.singleStudent').controller('StudentSnapshotPanelCtrl', StudentSnapshotPanelCtrl);
